import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Dumbbell, BarChart2, User, LogOut, ChevronUp, Youtube, History } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const NavItem = ({ to, icon: Icon, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) => cn(
      "flex flex-col md:flex-row items-center gap-1 md:gap-4 px-4 py-3 rounded-2xl transition-all duration-200 border border-transparent",
      isActive ? "text-[#A3E635] bg-[#A3E6351A] border-[#A3E63533]" : "text-slate-500 hover:text-slate-300"
    )}
  >
    <Icon size={22} />
    <span className="text-[10px] md:text-base font-bold tracking-tight">{label}</span>
  </NavLink>
);

const Layout = ({ children, title, subtitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const checkSession = () => {
      const savedRaw = localStorage.getItem('active_session');
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        setIsSessionActive(true);
        setIsPaused(!!saved.isPaused);
      } else {
        setIsSessionActive(false);
        setIsPaused(false);
      }
    };

    checkSession();
    // Check on every route change
    window.addEventListener('storage', checkSession);
    return () => window.removeEventListener('storage', checkSession);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#121214] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-20 bg-[#121214] border-b border-[#1C1C1E] z-50 px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-black tracking-tighter italic">TRAK-PLAY</h1>
          <div className={cn("text-[#A3E635]", isSessionActive && "animate-spin-slow", isPaused && "animate-paused")}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
          </div>
        </div>

        {title && (
          <div 
            className="hidden md:block text-right cursor-pointer select-none active:opacity-70 transition-opacity" 
            onClick={toggleFullScreen}
            title="Toggle full screen"
          >
            <h2 className="text-xl font-black uppercase italic tracking-tighter">{title}</h2>
            {subtitle && <p className="text-[10px] text-[#A3E635] font-bold uppercase">{subtitle}</p>}
          </div>
        )}
      </header>

      {/* Sidebar / Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#1C1C1E] border-t border-[#2C2C2E] px-4 py-2 md:top-20 md:bottom-0 md:left-0 md:w-64 md:border-r md:border-t-0 z-40">
        <div className="flex md:flex-col justify-around md:justify-start gap-1 md:gap-3 md:pt-6 relative">
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" />
          <NavItem to="/routine" icon={ClipboardList} label="Routines" />
          <NavItem to="/exercises" icon={Dumbbell} label="Exercises" />
          <NavItem to="/body" icon={BarChart2} label="Body Stats" />
          
          {/* Mobile Profile Menu */}
          <div className="md:hidden flex flex-col items-center">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={cn(
                "flex flex-col items-center gap-1 px-4 py-3 rounded-2xl transition-all duration-200 border border-transparent",
                showUserMenu ? "text-[#A3E635] bg-[#A3E6351A] border-[#A3E63533]" : "text-slate-500 hover:text-slate-300"
              )}
            >
              <User size={22} />
              <span className="text-[10px] font-bold tracking-tight">Profile</span>
            </button>
            
            {showUserMenu && (
              <div className="absolute bottom-20 right-4 left-4 bg-[#1C1C1E] border border-[#2C2C2E] rounded-2xl p-4 shadow-2xl flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="flex items-center gap-3 border-b border-[#2C2C2E] pb-3">
                  <div className="w-10 h-10 rounded-full bg-[#2C2C2E] flex items-center justify-center text-slate-400">
                    <User size={20} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold truncate">{user.username || 'User'}</span>
                    <span className="text-[10px] text-slate-500 uppercase">Active Session</span>
                  </div>
                </div>
                <NavLink 
                  to="/history"
                  className="flex items-center gap-3 text-slate-300 font-bold text-sm px-2 py-1 hover:text-[#A3E635] transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <History className="text-[#A3E635]" size={18} />
                  SESSION HISTORY
                </NavLink>
                <NavLink 
                  to="/youtube"
                  className="flex items-center gap-3 text-slate-300 font-bold text-sm px-2 py-1 hover:text-[#A3E635] transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <div className="relative flex items-center justify-center">
                    <Youtube className="text-[#A3E635]" size={18} />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-0 h-0 border-t-[3px] border-t-transparent border-l-[5px] border-l-[#1C1C1E] border-b-[3px] border-b-transparent ml-0.5"></div>
                    </div>
                  </div>
                  EMBED A VIDEO
                </NavLink>
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-3 text-red-500 font-bold text-sm px-2 py-1"
                >
                  <LogOut size={18} /> LOGOUT
                </button>
              </div>
            )}
          </div>
        </div>

        {/* User Profile (Desktop) */}
        <div className="hidden md:block absolute bottom-8 left-4 right-4">
          {showUserMenu && (
            <div className="absolute bottom-full left-0 right-0 mb-4 bg-[#1C1C1E] border border-[#2C2C2E] rounded-2xl p-4 shadow-2xl flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
              <NavLink 
                to="/history"
                className="flex items-center gap-3 text-slate-300 font-bold text-sm px-2 py-1 hover:text-[#A3E635] transition-colors"
                onClick={() => setShowUserMenu(false)}
              >
                <History className="text-[#A3E635]" size={18} />
                SESSION HISTORY
              </NavLink>
              <NavLink 
                to="/youtube"
                className="flex items-center gap-3 text-slate-300 font-bold text-sm px-2 py-1 hover:text-[#A3E635] transition-colors"
                onClick={() => setShowUserMenu(false)}
              >
                <div className="relative flex items-center justify-center">
                  <Youtube className="text-[#A3E635]" size={18} />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-0 h-0 border-t-[3px] border-t-transparent border-l-[5px] border-l-[#1C1C1E] border-b-[3px] border-b-transparent ml-0.5"></div>
                  </div>
                </div>
                EMBED A VIDEO
              </NavLink>
              <button 
                onClick={handleLogout}
                className="flex items-center gap-3 text-red-500 font-bold text-sm px-2 py-1"
              >
                <LogOut size={18} /> LOGOUT
              </button>
            </div>
          )}
          
          <button 
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={cn(
              "w-full flex items-center justify-between bg-[#2C2C2E] p-3 rounded-2xl transition-all duration-200 border border-transparent hover:bg-[#3C3C3E]",
              showUserMenu && "border-[#A3E63533] ring-1 ring-[#A3E63533]"
            )}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#3C3C3E] flex items-center justify-center text-slate-400">
                <User size={20} />
              </div>
              <div className="flex flex-col text-left">
                <span className="text-sm font-bold truncate w-24">{user.username || 'User'}</span>
                <span className="text-[10px] text-slate-500 uppercase">Profile</span>
              </div>
            </div>
            <ChevronUp size={16} className={cn("text-slate-400 transition-transform duration-200", showUserMenu && "rotate-180")} />
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-24 md:pb-8 md:pl-72 px-4 md:px-8 max-w-7xl mx-auto">
        {children}
      </main>
    </div>
  );
};

export default Layout;
